/**
 * Column definitions for the Posts table (§12.3).
 *
 * Ids, labels, alignment and sortability all come from `packages/shared/columns.ts` — this module
 * only supplies the cell renderers. Every metric cell renders an `Explained`/`MultiplierDTO`
 * through `<Num>`/`<MultiplierBadge>`, so a raw number cannot reach the screen (§12.5 rule 2).
 */

import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  POSTS_COLUMNS,
  type ContentType,
  type MediaRefDTO,
  type PostRowDTO,
  type PostsColumnId,
} from '@lw/shared';
import {
  BookmarkFilledIcon,
  BookmarkIcon,
  DocIcon,
  MultiplierBadge,
  Num,
  PlayIcon,
  cx,
} from '../../../components';
import { mediaUrl } from '../../../api/media';
import { formatDateTime } from '../../../lib/datetime';
import { formatInteger } from '../../../lib/format';
import { CONTENT_TYPE_LABEL, POST_TYPE_LABEL } from '../../../lib/labels';
import { useSaveDialog } from '../../saved/SaveDialog';
import { UnmappedBadge } from '../postRender';

/** Pixel widths per column; the preview column takes whatever is left. */
export const COLUMN_WIDTH: Partial<Record<PostsColumnId, number>> = {
  author: 160,
  preview: 0,
  publishedAt: 170,
  postType: 150,
  subtype: 130,
  contentType: 140,
  reactions: 100,
  // Multiplier columns: a two-character header ("R ×") over a chip. 76px is the floor that still
  // fits a three-digit chip ("134.5×" plus its pill padding) inside the 12px cell gutters.
  reactionsX: 76,
  comments: 100,
  commentsX: 76,
  shares: 90,
  sharesX: 76,
  saves: 90,
  savesX: 76,
  totalEngagement: 120,
  authorFollowers: 100,
  engagementRate: 100,
  words: 80,
  chars: 90,
  hasMedia: 70,
  edited: 80,
  mentions: 90,
  link: 70,
  firstSeenAt: 170,
  lastUpdatedAt: 170,
  metricsAsOf: 170,
  possiblyDeleted: 120,
  savedAt: 170,
};

export function columnWidth(id: PostsColumnId): number {
  return COLUMN_WIDTH[id] ?? 120;
}

export type ColumnContext = { timezone: string; mediaEnabled: boolean };

/**
 * The 24px square that closes the preview cell.
 *
 * A plain `<img>`, not a `MediaBlock`: that component's placeholder/retry chrome is built for
 * blocks ≥96px, and at this size the only degraded state worth rendering is a glyph tile.
 *
 * The treatment keys off the row's **`contentType`**, never off `MediaRefDTO.kind` — the media
 * role `document` maps to kind `image`, because the cached asset is the cover page (§9.3). Video
 * and document rows therefore always show a tile (glyph when there is nothing to load); every
 * other row shows its picture or nothing at all, leaving the cell as it was.
 *
 * Decoration only, hence `aria-hidden`: the Content type and Has-media columns carry the meaning.
 */
export function PostThumb({
  thumb,
  contentType,
  mediaEnabled,
}: {
  thumb: MediaRefDTO | null;
  contentType: ContentType;
  mediaEnabled: boolean;
}) {
  // Keyed to the asset, not to the component: virtualized rows recycle instances, so a plain
  // boolean would carry one row's load failure onto the next row that lands on this key.
  const [failedAssetId, setFailedAssetId] = useState<number | null>(null);

  const marked = contentType === 'video' || contentType === 'document';
  const loadable =
    thumb !== null &&
    mediaEnabled &&
    thumb.status !== 'failed' &&
    failedAssetId !== thumb.assetId;

  if (!loadable && !marked) return null;

  const Glyph = contentType === 'document' ? DocIcon : PlayIcon;
  return (
    <span className={cx('cell-thumb', !loadable && 'cell-thumb--glyph')} aria-hidden="true">
      {loadable && thumb !== null ? (
        <img
          src={mediaUrl(thumb.assetId)}
          alt=""
          loading="lazy"
          onError={() => setFailedAssetId(thumb.assetId)}
        />
      ) : (
        <Glyph size={13} />
      )}
      {loadable && marked ? (
        <span className="cell-thumb__badge">
          <Glyph size={8} />
        </span>
      ) : null}
    </span>
  );
}

/**
 * The row-level save control (SPEC `saved-posts` § Posts table).
 *
 * A saved row states when it was saved and shows a filled bookmark; an unsaved one shows a quiet
 * outlined one that only appears on row hover, so the column reads as data rather than as a column
 * of buttons. Either way the cell opens the same popup every other surface opens — the table never
 * saves or unsaves on a single click.
 */
function SavedCell({ row }: { row: PostRowDTO }) {
  const { openSave } = useSaveDialog();
  return (
    <button
      type="button"
      className={cx('cell-save', row.savedAt === null && 'cell-save--empty')}
      aria-label={row.savedAt === null ? 'Save this post' : `Saved ${row.savedAtDisplay ?? ''}`}
      onClick={() => openSave(row.id)}
    >
      {row.savedAt === null ? (
        <BookmarkIcon size={13} />
      ) : (
        <>
          <BookmarkFilledIcon size={12} />
          <span className="cell-ellipsis">{row.savedAtDisplay}</span>
        </>
      )}
    </button>
  );
}

function cellRenderer(id: PostsColumnId, context: ColumnContext) {
  return function Cell({ row }: { row: PostRowDTO }) {
    switch (id) {
      case 'author':
        return <span className="cell-ellipsis">{row.authorName}</span>;
      case 'preview':
        return (
          <span className="cell-preview">
            {row.possiblyDeleted ? <span className="deleted-dot" title="Possibly deleted" /> : null}
            <span className="cell-ellipsis">{row.previewText}</span>
            <UnmappedBadge keys={row.unmappedKeys} />
            <PostThumb
              thumb={row.thumb}
              contentType={row.contentType}
              mediaEnabled={context.mediaEnabled}
            />
          </span>
        );
      case 'publishedAt':
        return <span>{row.publishedAtDisplay}</span>;
      case 'postType':
        // `typeDisplay` already folds the subtype in ("Original · Link share"), which is what the
        // dense grid wants; the label map is only the fallback for a pre-v3 payload.
        return <span className="cell-ellipsis">{row.typeDisplay || POST_TYPE_LABEL[row.postType]}</span>;
      case 'subtype':
        return <span className="cell-ellipsis">{row.subtype ?? '—'}</span>;
      case 'contentType':
        return <span>{CONTENT_TYPE_LABEL[row.contentType]}</span>;
      case 'reactions':
        return <Num value={row.reactions} withTooltip={false} />;
      case 'comments':
        return <Num value={row.comments} withTooltip={false} />;
      case 'shares':
        return <Num value={row.shares} withTooltip={false} />;
      case 'saves':
        return <Num value={row.saves} withTooltip={false} />;
      case 'reactionsX':
        // Dense grid: the tooltip affordance appears only where the PRD demands it — an
        // insufficient baseline must explain its "—". Full tooltips live in the post preview.
        return <MultiplierBadge value={row.reactionsX} withTooltip={row.reactionsX.insufficient} />;
      case 'commentsX':
        return <MultiplierBadge value={row.commentsX} withTooltip={row.commentsX.insufficient} />;
      case 'sharesX':
        return <MultiplierBadge value={row.sharesX} withTooltip={row.sharesX.insufficient} />;
      case 'savesX':
        return <MultiplierBadge value={row.savesX} withTooltip={row.savesX.insufficient} />;
      case 'totalEngagement':
        return <Num value={row.totalEngagement} withTooltip={false} />;
      case 'authorFollowers':
        return <Num value={row.authorFollowers} withTooltip />;
      case 'engagementRate':
        // The (i) stays on: a `—` here has a reason (repost, no follower count) only the tooltip gives.
        return <Num value={row.engagementRate} withTooltip />;
      case 'words':
        return <span>{formatInteger(row.words)}</span>;
      case 'chars':
        return <span>{formatInteger(row.chars)}</span>;
      case 'hasMedia':
        return <span aria-label={row.hasMedia ? 'has media' : 'no media'}>{row.hasMedia ? '●' : '—'}</span>;
      case 'edited':
        return <span aria-label={row.edited ? 'edited' : 'not edited'}>{row.edited ? '●' : '—'}</span>;
      case 'mentions':
        return <span>{formatInteger(row.mentions)}</span>;
      case 'link':
        return (
          <span aria-label={row.hasLink ? 'has external link' : 'no external link'}>
            {row.hasLink ? '●' : '—'}
          </span>
        );
      case 'firstSeenAt':
        return <span>{formatDateTime(row.firstSeenAt, context.timezone)}</span>;
      case 'lastUpdatedAt':
        return <span>{formatDateTime(row.lastUpdatedAt, context.timezone)}</span>;
      case 'metricsAsOf':
        return <span>{formatDateTime(row.metricsAsOf, context.timezone)}</span>;
      case 'possiblyDeleted':
        return row.possiblyDeleted ? <span className="deleted-text">Possibly deleted</span> : <span>—</span>;
      case 'savedAt':
        return <SavedCell row={row} />;
      default:
        return null;
    }
  };
}

/**
 * TanStack Table column defs. Sorting is `manualSorting` — the header click writes the shared sort
 * state and the server re-orders (§5.4), so no client comparator exists.
 */
export function buildPostsColumns(context: ColumnContext): ColumnDef<PostRowDTO>[] {
  return POSTS_COLUMNS.map((column) => {
    const Cell = cellRenderer(column.id, context);
    return {
      id: column.id,
      header: column.label,
      enableSorting: column.sortable,
      cell: (info) => <Cell row={info.row.original} />,
    } satisfies ColumnDef<PostRowDTO>;
  });
}
